import React, { FC, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import Copy from 'Src/components/Copy';
import { Check, CheckSplitItemsRequestDef } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useToast } from 'Src/feature/toasts/useToast';

import styles from './CheckItemSplitModal.module.scss';

const sectionId = 'CheckItemSplitModal';
const TEST_ID = {
  root: 'CheckItemSplitModal-root',
} as const;

interface CheckItemSplitModalContentProps {
  checkId?: number;
  items: Check['items'];
  onCancel: () => void;
  onAfterSave: () => void;
}

interface CheckItemSplitModalProps extends CheckItemSplitModalContentProps {
  open: boolean;
}

const { Form, Field } = getTypedFormComponents<{ numParts: string }>();

const CheckItemSplitModalContent: FC<CheckItemSplitModalContentProps> = ({ checkId, items, onCancel, onAfterSave }) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const toast = useToast();

  const selectedUnSplitCheckItems = useMemo(() => items.filter(({ splitBy }) => !splitBy), [items]);
  const singleItem = selectedUnSplitCheckItems.length === 1 ? selectedUnSplitCheckItems[0] : undefined;

  const splitCheck = useCheck.mutate.splitCheck();

  const form = useForm<{ numParts: string }>({
    schema: object().shape({
      numParts: number().label(t('checkItemSplit.labels.parts')).transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().min(2).max(99).required(),
    }),
  });

  const onCommit = useCallback(async () => {
    const { values, isValid } = await form.action.submit();
    if (!isValid) return log.info('modal.splitItem.onCommit()', 'form !isValid');
    if (!values) return log.info('modal.splitItem.onCommit()', 'form !values');
    const csir: CheckSplitItemsRequestDef = {
      originCheck: true,
      newCheck: false,
      items: [],
    };
    selectedUnSplitCheckItems.forEach((selectedItem) => {
      for (let i = 0; i < parseInt(values.numParts); i++) csir.items.push({
        itemId: selectedItem.id,
        type: 'Portion',
        seatNames: selectedItem.seatNames,
      });
    });
    const csr = { checkSplits: [csir] };
    const payload = {
      checkId: checkId as number,
      split: csr,
    };
    splitCheck.mutate(payload, {
      onSuccess: () => {
        form.action.reset();
        onAfterSave();
      },
      onError: (err) => {
        toast.show.serviceError(err, { id: `${sectionId}.splitCheck` });
      },
    });
  }, [checkId, form.action, log, onAfterSave, selectedUnSplitCheckItems, splitCheck, toast.show]);

  const handleCancel = () => {
    form.action.reset();
    onCancel();
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle
          copy={(
            <Trans
              i18nKey="checkItemSplit.label"
              count={selectedUnSplitCheckItems.length}
            />
          )}
        />
        <Copy variant="body" size="lg" weight="semi-bold" >
          <Trans
            i18nKey="checkItemSplit.subTitle"
            count={selectedUnSplitCheckItems.length}
            values={{
              itemName: singleItem?.menuItem.name,
              itemQuantity: singleItem?.quantity,
              itemCount: selectedUnSplitCheckItems.length,
            }}
            components={{
              ItemName: <span className={styles['itemName']} />
            }}
          />
        </Copy>
      </ModalHeader>

      <ModalContent>
        <Form usedForm={form} omitChangeTracking>
          <Field
            type="unit"
            name="numParts"
            unit={t('checkItemSplit.labels.parts')}
          />
        </Form>
      </ModalContent>

      <ModalActions>
        <ModalAction
          label={t('checkItemSplit.actions.cancel')}
          onClick={handleCancel}
          color="secondary"
        />
        <ModalAction
          label={t('checkItemSplit.actions.save')}
          onClick={onCommit}
          color="primary"
          disabled={!form.state.isValid}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const CheckItemSplitModal: FC<CheckItemSplitModalProps> = ({ open, checkId, items, onCancel, onAfterSave }) => (
  <Modal
    data-testid={TEST_ID.root}
    open={open}
    onBackDropClick={onCancel}
    variant="grow"
  >
    <CheckItemSplitModalContent
      checkId={checkId}
      items={items}
      onCancel={onCancel}
      onAfterSave={onAfterSave}
    />
  </Modal>
);

export { CheckItemSplitModal, sectionId, TEST_ID };
